import React, { FC, useCallback, useState } from 'react';
import TableCell from '@mui/material/TableCell';
import {
	Chip,
	ChipProps,
	CircularProgress,
	Icon,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import styled from 'styled-components';
import MainTable, { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { TableHeader } from '@Features/generics/generics.type';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import {
	videoImportsAddUnit,
	videoImportsDelete,
	videoImportsFetchAll,
	videoImportsRetry,
	videoImportErrorMessage,
} from '../Slices/VideoImports.slice';
import { VideoImport, VideoImportStatus } from '../Types/VideoImports.types';

const tableHeaders: Array<TableHeader> = [
	// Widths must leave every nowrap uppercase header label room at the
	// table's min width — MainTable is tableLayout:fixed, so a too-narrow
	// column lets its label paint into the neighbor cell.
	{ displayName: 'Title', fieldName: 'title', width: '18%', canSort: false },
	{ displayName: 'Source', fieldName: 'source_type', width: '8%', canSort: false },
	{ displayName: 'Provider', fieldName: 'provider', width: '9%', canSort: false },
	{ displayName: 'Status', fieldName: 'status', width: '10%', canSort: false },
	{ displayName: 'ETA', fieldName: 'encode_progress', width: '10%', canSort: false },
	{ displayName: 'Duration', fieldName: 'duration', width: '9%', canSort: false },
	{ displayName: 'Link', fieldName: 'source_url', width: '9%', canSort: false },
	{ displayName: 'Created', fieldName: 'created_at', width: '14%', canSort: false },
];

const SOURCE_LABELS: Record<VideoImport['sourceType'], string> = {
	gdrive: 'Google Drive',
	onedrive: 'OneDrive',
	youtube: 'YouTube',
	direct: 'Direct link',
};

const PROVIDER_LABELS: Record<VideoImport['provider'], string> = {
	bunny: 'Bunny Stream',
	vdocipher: 'VdoCipher',
	// Document (PDF) rows — transferred into the platform's S3 store.
	storage: 'Storage (PDF)',
};

const STATUS_CHIPS: Record<
	VideoImportStatus,
	{ label: string; color: ChipProps['color']; variant?: ChipProps['variant'] }
> = {
	pending: { label: 'Pending', color: 'default' },
	downloading: { label: 'Downloading', color: 'info' },
	submitted: { label: 'Submitted', color: 'info' },
	processing: { label: 'Processing', color: 'warning' },
	// Playable already — Bunny finishes the remaining renditions in background.
	transcoding: { label: 'Playable — finishing', color: 'success', variant: 'outlined' },
	ready: { label: 'Ready', color: 'success' },
	failed: { label: 'Failed', color: 'error' },
	unit_created: { label: 'Added as unit', color: 'success', variant: 'outlined' },
};

/** mm:ss from seconds (minutes keep counting past 59 — unambiguous). */
const formatDuration = (seconds: number | null) => {
	if (!seconds || seconds <= 0) return '-';
	const minutes = Math.floor(seconds / 60);
	const rest = Math.round(seconds % 60);
	return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

const minutesSince = (iso: string) =>
	Math.max((Date.now() - new Date(iso).getTime()) / 60_000, 0);

/** Compact clickable source: hostname shown, full URL on hover, opens in a new tab. */
const sourceHost = (url: string) => {
	try {
		return new URL(url).hostname.replace(/^www\./, '');
	} catch {
		return url;
	}
};

const StatusCell: FC<{ row: VideoImport }> = ({ row }) => {
	const config = STATUS_CHIPS[row.status] ?? STATUS_CHIPS.pending;
	const chip = (
		<Chip
			size="small"
			label={config.label}
			color={config.color}
			variant={config.variant ?? 'filled'}
		/>
	);
	if (row.status === 'failed' && row.errorMessage) {
		return <Tooltip title={row.errorMessage}>{chip}</Tooltip>;
	}
	return chip;
};

/** Compact ring: x% inside the circle, time/stage under it (full text on hover). */
const ProgressRing: FC<{
	percent?: number;
	caption?: string;
	captionTitle?: string;
}> = ({ percent, caption, captionTitle }) => (
	<FlexLayout
		flexDirection="column"
		alignItems="center"
		gridGap="0.15rem"
		width="100%"
	>
		<RingWrap>
			<CircularProgress
				size={38}
				thickness={4}
				variant={percent === undefined ? 'indeterminate' : 'determinate'}
				value={percent ?? 0}
			/>
			{percent !== undefined && <RingText>{percent}%</RingText>}
		</RingWrap>
		{caption && (
			<ProgressCaption variant="caption" title={captionTitle ?? caption}>
				{caption}
			</ProgressCaption>
		)}
	</FlexLayout>
);

const ProgressCell: FC<{ row: VideoImport }> = ({ row }) => {
	// Documents transfer inline and never encode — no ring, no ETA.
	if (row.kind === 'document') {
		return <span>-</span>;
	}
	// Done (Ready or already a unit) = full ring, per design.
	if (row.status === 'ready' || row.status === 'unit_created') {
		return <ProgressRing percent={100} />;
	}
	// YouTube pipeline — the stage under the ring says exactly where it is
	// (retrieving info / downloading with speed+ETA / combining / uploading).
	if (row.status === 'downloading') {
		return (
			<ProgressRing
				percent={row.encodeProgress ?? undefined}
				caption={row.stageDetail || 'Starting…'}
			/>
		);
	}
	if (row.status === 'submitted') {
		return <ProgressRing caption="Queued" />;
	}
	const inFlight = row.status === 'processing' || row.status === 'transcoding';
	if (!inFlight) return <span>-</span>;

	if (row.provider === 'bunny') {
		const progress = row.encodeProgress ?? 0;
		// ETA = elapsed × (100 − p) / p — only meaningful once p ≥ 5.
		let eta: number | null = null;
		if (progress >= 5) {
			const elapsed = minutesSince(row.createdAt);
			eta = Math.min(
				Math.max(Math.round((elapsed * (100 - progress)) / progress), 1),
				120
			);
		}
		return (
			<ProgressRing
				percent={progress}
				caption={eta !== null ? `~${eta} min` : undefined}
			/>
		);
	}
	// VdoCipher exposes no progress — spinner + elapsed, no fake percentage.
	const elapsed = Math.round(minutesSince(row.createdAt));
	return (
		<ProgressRing
			caption={`${elapsed} min`}
			captionTitle={`${elapsed} min elapsed · typically ~9 min/GB`}
		/>
	);
};

type Props = {
	courseId: number;
	onChanged: () => void;
};

const VideoImportsTable: FC<Props> = ({ courseId, onChanged }) => {
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { list, meta } = useTypedSelector((state) => state.VideoImports);
	const [pendingDelete, setPendingDelete] = useState<VideoImport | null>(null);
	const [addingUnitId, setAddingUnitId] = useState<number | null>(null);

	const onPageRequestCallback = useCallback(
		({ activePage, perPage }: onPageChangeRequestMeta) => {
			dispatch(
				videoImportsFetchAll({
					course_id: courseId,
					page: activePage,
					per_page: perPage ?? 10,
				})
			);
		},
		[courseId, dispatch]
	);

	// No dialog on purpose: the unit takes the import's title, lands at the
	// end of the course (order auto-assigns), unlimited plays. All of that
	// stays editable on the unit afterwards.
	const onAddUnit = async (row: VideoImport) => {
		if (addingUnitId) return;
		setAddingUnitId(row.id);
		try {
			await dispatch(
				videoImportsAddUnit({
					id: row.id,
					course_id: courseId,
					name: row.title,
				})
			).unwrap();
			displaySnackbar('success', 'Added as unit');
			onChanged();
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoImportErrorMessage(error, 'Unable to add the import as a unit')
			);
		} finally {
			setAddingUnitId(null);
		}
	};

	const onRetry = async (row: VideoImport) => {
		try {
			await dispatch(
				videoImportsRetry({ id: row.id, course_id: courseId })
			).unwrap();
			displaySnackbar('success', 'Video import retried');
			onChanged();
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoImportErrorMessage(error, 'Unable to retry the video import')
			);
		}
	};

	const onConfirmDelete = async (close: boolean, confirm?: boolean) => {
		const row = pendingDelete;
		setPendingDelete(null);
		if (!confirm || !row) return;
		try {
			await dispatch(
				videoImportsDelete({ id: row.id, course_id: courseId })
			).unwrap();
			displaySnackbar('success', 'Video import deleted');
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoImportErrorMessage(error, 'Unable to delete the video import')
			);
		}
		onChanged();
	};

	return (
		<CompactHeaders>
			{/* No tableMinWidth override: anything wider than the tab panel makes
			    the PAGE scroll horizontally. Nine columns need the smaller header
			    font below to keep the nowrap labels apart. */}
			<MainTable
				tableFilters={[]}
				dateFilter={false}
				tableHeads={tableHeaders}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestCallback}
				RowsperPage={meta?.per_page}
				pageNum={meta?.current_page}
				$hasActions
				total={meta?.total}
				renderItems={() => {
					if (list.length === 0)
						return (
							<MainTableRow>
								<TableCell colSpan={tableHeaders.length + 1}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					return list.map((row) => (
						<MainTableRow key={row.id} hover>
							<TableCell>
								<FlexLayout
									alignItems="center"
									justifyContent="center"
									gridGap="0.5rem"
								>
									{row.thumbnailUrl && (
										<Thumbnail src={row.thumbnailUrl} alt={row.title} />
									)}
									<TitleText title={row.title}>{row.title || '-'}</TitleText>
								</FlexLayout>
							</TableCell>
							<TableCell title={SOURCE_LABELS[row.sourceType] ?? row.sourceType}>
							{SOURCE_LABELS[row.sourceType] ?? row.sourceType}
							{/* ffprobe-verified height of the downloaded source
							    (YouTube worker only) — the quality-check tag. */}
							{row.sourceHeight ? (
								<Chip
									size="small"
									variant="outlined"
									label={`${row.sourceHeight}p`}
									sx={{ marginLeft: '0.4rem' }}
								/>
							) : null}
						</TableCell>
							<TableCell title={PROVIDER_LABELS[row.provider] ?? row.provider}>
								{PROVIDER_LABELS[row.provider] ?? row.provider}
							</TableCell>
							<TableCell>
								<StatusCell row={row} />
							</TableCell>
							<TableCell>
								<ProgressCell row={row} />
							</TableCell>
							<TableCell>{formatDuration(row.duration)}</TableCell>
							<TableCell>
								<SourceLink
									href={row.sourceUrl}
									target="_blank"
									rel="noreferrer"
									title={row.sourceUrl}
								>
									{sourceHost(row.sourceUrl)}
								</SourceLink>
							</TableCell>
							<TableCell>
								<DateTimeFormatter date={new Date(row.createdAt)} />
							</TableCell>
							<TableCell>
								<FlexLayout justifyContent="center" gridGap="0.25rem">
									{/* Added = unit_id set (independent of status, so a
									    mid-transcode add keeps its live progress visible). */}
									{row.unitId ? (
										<Tooltip title="Added as unit">
											<Icon fontSize="small" color="success">
												check_circle
											</Icon>
										</Tooltip>
									) : (
										(row.status === 'ready' || row.status === 'transcoding') && (
											<Tooltip
												title={
													row.status === 'transcoding'
														? 'Add as unit — playable now, remaining qualities finish in the background'
														: 'Add as unit (added at the end of the course)'
												}
											>
												<IconButton
													size="small"
													color="primary"
													disabled={addingUnitId !== null}
													onClick={() => onAddUnit(row)}
												>
													<Icon fontSize="small">playlist_add</Icon>
												</IconButton>
											</Tooltip>
										)
									)}
									{row.status === 'failed' && (
										<Tooltip title="Retry">
											<IconButton size="small" onClick={() => onRetry(row)}>
												<Icon fontSize="small">replay</Icon>
											</IconButton>
										</Tooltip>
									)}
									<Tooltip title="Delete">
										<IconButton
											size="small"
											onClick={() => setPendingDelete(row)}
										>
											<Icon fontSize="small">delete</Icon>
										</IconButton>
									</Tooltip>
								</FlexLayout>
							</TableCell>
						</MainTableRow>
					));
				}}
			/>

			<EdConfirmModal
				open={!!pendingDelete}
				handleClose={onConfirmDelete}
				text={
					pendingDelete
						? `Are you sure you want to delete import "${pendingDelete.title}"?${
								pendingDelete.status !== 'unit_created' &&
								pendingDelete.kind !== 'document'
									? ' The provider copy will be removed too.'
									: ''
						  }`
						: ''
				}
			/>
		</CompactHeaders>
	);
};

/* Nine columns: MainTable's 1rem nowrap uppercase headers collide, so this
   table alone gets compact header typography (and tighter cell padding). */
const CompactHeaders = styled.div`
	th.MuiTableCell-head {
		font-size: 0.78rem;
		letter-spacing: 0.03em;
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}

	td.MuiTableCell-root {
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}
`;

const RingWrap = styled.span`
	position: relative;
	display: inline-flex;
`;

const RingText = styled.span`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.62rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.7);
`;

const SourceLink = styled.a`
	color: #6c63ff;
	text-decoration: none;
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;

	&:hover {
		text-decoration: underline;
	}
`;

const Thumbnail = styled.img`
	width: 3rem;
	height: 1.75rem;
	object-fit: cover;
	border-radius: 4px;
	flex-shrink: 0;
`;

const TitleText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ProgressCaption = styled(Typography)`
	color: rgba(0, 0, 0, 0.6);
	white-space: nowrap;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export default VideoImportsTable;
